<template>
<div>
  <div class="donapp-navbar white-bg z-1">
    <div class="donapp-navbar-header">
      <dates-filter
        :canDateRange="true"
        startPlaceholder="00/00/0000"
        endPlaceholder="00/00/0000"
        :maxStartDate="today"
        :minEndDate="startDate"
        :startDate="startDate"
        :endDate="endDate"
        headerGradient="merchant"
        :onChange="onDateRangeChange"/>
    </div>
  </div>
  <progress-bar class="progress-bar" :hidden="loading ? false : true" />
  <div class="container-fluid">
    <div class="row">
      <div class="col-lg-3 col-md-6 col-sm-6">
        <stats-small-card
          icon="icon-n_transaction"
          :title="$t('labels.num_sales')"
          :value="mainStats.numTransactions || 0"
          :partials="[]"
        />
      </div>
      <div class="col-lg-3 col-md-6 col-sm-6">
        <stats-small-card
          icon="icon-payment_list"
          :title="$t('labels.gross_sales')"
          :value="formatCurrency(mainStats.totTransactions)"
          :partials="[]"
        />
      </div>
      <div class="col-lg-3 col-md-6 col-sm-6">
        <stats-small-card
          icon="icon-causes"
          :title="$t('labels.tot_donations')"
          :value="formatCurrency(mainStats.totDonations)"
          :partials="[]"
        />
      </div>
      <div class="col-lg-3 col-md-6 col-sm-6">
        <stats-small-card
          icon="icon-issues"
          :title="$t('labels.num_issues')"
          :value="mainStats.numIssues || 0"
          :partials="[]"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-md-6">
        <chart-card type="line" :title="$t('charts.num_sales.title')" :data="transactionsChartData" :options="options" :period="period"/>
      </div>
      <div class="col-md-6">
        <chart-card type="line" :title="$t('charts.gross_sales.title')" :data="grossTransactionsChartData" :options="options" :period="period"/>
      </div>
      <div class="col-md-4">
        <chart-card type="doughnut" :title="$t('charts.price_range.title')" :data="statsPriceRange" :options="options" :backgrounds="statsPriceRange.datasets[0].backgroundColor" :legendItems="statsPriceRange.labels" :period="period"/>
      </div>
      <div class="col-md-4">
        <chart-card type="doughnut" :title="$t('charts.scanned.title')" :data="statsScannedVsNotScanned" :options="options" :backgrounds="statsScannedVsNotScanned.datasets[0].backgroundColor" :legendItems="statsScannedVsNotScanned.labels" :period="period"/>
      </div>
      <div class="col-md-4">
        <chart-card type="doughnut" :title="$t('charts.returning.title')" :data="statsReturningUsers" :options="options" :backgrounds="statsReturningUsers.datasets[0].backgroundColor" :legendItems="statsReturningUsers.labels" :period="period"/>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import ProgressBar from '@/components/shared/ProgressBar'
import DatesFilter from '@/components/shared/inputs/DatesFilter'
import StatsSmallCard from '@/components/shared/StatsSmallCard'
import ChartCard from '@/components/shared/ChartCard'
import apiService from '@/services/ApiService'
import lineChartService from '@/services/LineChartService'
import moment from 'moment'
import * as _ from 'lodash'
import formatter from '@/mixins/Formatter'

export default {
  name: 'merchant-dashboard',
  components: {
    DatesFilter,
    StatsSmallCard,
    ChartCard,
    ProgressBar
  },
  mixins: [formatter],
  mounted: async function() {
    moment.locale('it')
    this.getAnalytics()
  },
  data() {
    return {
      today: moment.utc(),
      startDate: moment.utc().subtract(1, 'days'),
      startDateInterval: '',
      endDateInterval: '',
      endDate: moment.utc(),
      analytics: {
        mainStats: {},
        statsReturningUsers: {},
        statsPriceRange: {},
        statsScannedVsNotScanned: {}
      },
      options: {},
      loading: false
    }
  },
  methods: {
    getAnalytics: async function() {
      try {
        this.loading = true
        /* eslint-disable */
        const endDate = this.endDate
          ? moment(this.endDate)
              .add(1, 'days')
              .toString()
          : null
        /* eslint-enable */
        const result = await apiService.getAnalytics('merchant', {
          startDate: this.startDate ? this.startDate.toString() : null,
          endDate
        })
        this.startDateInterval = this.startDate
        this.endDateInterval = this.endDate
        this.analytics = result.data.data
      } catch (error) {
        this.$toasted.error(this.$i18n.t('errors.something_wrong'))
      }
      this.loading = false
    },
    onDateRangeChange: function(changedDate, value) {
      if (changedDate === 'startDate') this.startDate = value
      else this.endDate = value
      this.getAnalytics()
    }
  },
  computed: {
    period: function() {
      return (
        (this.startDate ? this.startDate.format("DD MMM 'YY") : '-') +
        ' - ' +
        (this.endDate ? this.endDate.format("DD MMM 'YY") : '-')
      )
    },
    mainStats: function() {
      return this.analytics.mainStats
    },
    totDonations: function() {
      return this.mainStats.totDonations || 0
    },
    totTransactions: function() {
      return this.mainStats.totTransactions || 0
    },
    grossTransactionsChartData: function() {
      return lineChartService.extractLineChartData(
        this.analytics.results,
        'amount',
        this.startDate ? this.startDate : _.minBy(this.analytics.results, 'date')['date'],
        this.endDate ? this.endDate : _.maxBy(this.analytics.results, 'date')['date']
      )
    },
    transactionsChartData: function() {
      return lineChartService.extractLineChartData(
        this.analytics.results,
        null,
        this.startDate ? this.startDate : _.minBy(this.analytics.results, 'date')['date'],
        this.endDate ? this.endDate : _.maxBy(this.analytics.results, 'date')['date']
      )
    },
    // TODO: mettere global property border color transparent and background color
    statsScannedVsNotScanned: function() {
      const statsScannedVsNotScanned = this.analytics.statsScannedVsNotScanned
      return {
        labels: [this.$i18n.t('charts.scanned.scanned'), this.$i18n.t('charts.scanned.not_scanned')],
        datasets: [
          {
            borderColor: ['transparent', 'transparent'],
            backgroundColor: ['#ace7d9', '#72d2bf'],
            data: Object.keys(statsScannedVsNotScanned).map(key => statsScannedVsNotScanned[key])
          }
        ]
      }
    },
    statsPriceRange: function() {
      const statsPriceRange = this.analytics.statsPriceRange
      return {
        labels: ['< 10', '10 - 50', '50 - 200', '> 200'],
        datasets: [
          {
            borderColor: ['transparent', 'transparent', 'transparent', 'transparent', 'transparent'],
            backgroundColor: ['#ace7d9', '#72d2bf', '#2b7d70', '#1a4440'],
            data: Object.keys(statsPriceRange).map(key => statsPriceRange[key])
          }
        ]
      }
    },
    statsReturningUsers: function() {
      const statsReturningUsers = this.analytics.statsReturningUsers
      return {
        labels: [
          this.$i18n.t('charts.returning.returning'),
          this.$i18n.t('charts.returning.onetime'),
          this.$i18n.t('charts.returning.unknown')
        ],
        datasets: [
          {
            borderColor: ['transparent', 'transparent', 'transparent'],
            backgroundColor: ['#ace7d9', '#72d2bf', '#1a4440'],
            data: Object.keys(statsReturningUsers).map(key => statsReturningUsers[key])
          }
        ]
      }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import '../../scss/config.scss';

.stats-m-i {
  color: white;
}

.card .card-header {
  margin: -20px 17px 0;
}

.donapp-navbar {
  display: flex;
  height: $topbarHeight;
  justify-content: space-between;
  align-items: center;
  padding: 0px 17px;
  border-radius: 0px;
  @include shadow();
}

.type-filters {
  display: flex;
  align-items: center;
}

.card-header.line-chart-card {
  height: 196px !important;
  position: relative;

  .line-chart {
    height: 162px !important;
    z-index: 1;
  }

  .line-chart-shadow {
    height: 162px !important;
    width: calc(100% - 54px);
    position: absolute;
    left: 37px;
    top: 20px;
    z-index: 0;
  }
}

.card-header.doughnut-chart-card {
  position: relative;
  width: calc(100% - 34px);
}

.radio label {
  cursor: pointer;
  padding-left: 31px;
  padding-right: 16px;
  position: relative;
  color: $grey;
}

.checkbox + .checkbox,
.radio + .radio {
  margin: 0px;
}
</style>



// WEBPACK FOOTER //
// src/components/merchants/Dashboard.vue